/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 25: Table List & Bullet Styling
// ═══════════════════════════════════════════════════════════════════════════
//
// FULLY INSTRUMENTED VERSION — every paragraph in every table cell is logged
// with its full decision trace, so we can see exactly why a given paragraph
// (e.g. "Inclusion Criteria:") is or isn't being demoted.
//
// Six processing phases per paragraph:
//   Phase 1 — Universal skips         (empty / excluded styles / Heading X)
//   Phase 2 — Header DEMOTION         (title-case + ends-with-":" headers)
//   Phase 3 — Already-correct skip    (style already matches the target)
//   Phase 4 — Wrong-context fix       (body list style sitting in a table)
//   Phase 5 — Existing Word list      (Word.js reports listItem)
//   Phase 6 — Pattern detection       (text starts with bullet/1./a.)
//
// Before the main pass, a PRE-SCAN walks every cell paragraph in the document
// and dumps every header candidate (text ending with ":" that's short and
// title-case) with its full state — including raw paragraph OOXML. This is
// the diagnostic we'll use to explain to the manager why the previous
// attempts didn't catch "Inclusion Criteria:".
// ═══════════════════════════════════════════════════════════════════════════

import { safeAlert, reportResult, RunDetail, ListGroupFlagger, ListGroupItem } from '../runs/common';
import {
  shouldExclude,
  isAnyBodyListStyle, isAnyTableListStyle,
  isBodyBulletStyle, isBodyNumberStyle, isBodyAlphaStyle,
  getLevelFromBodyStyle,
  detectBullet, detectNumberedList, detectAlphaList,
  getBulletLevel, getIndentLevel,
  classifyListItem,
  getTargetStyle,
} from '../runs/listDetection';
import { getConfig } from '../taskpane/configService';

// ── S4323 fix: named type alias replaces all inline union literals ──
type ListType = "bullet" | "number" | "alpha";

// ── Per-run mutable counters bundled into one object for clarity ──
interface RunCounters {
  totalChecked: number;
  existingStyled: number;
  patternsConverted: number;
  skippedCount: number;
  wrongContextFixed: number;
  headersDemoted: number;
  // Detailed skip tallies
  skipEmpty: number;
  skipShouldExclude: number;
  skipBuiltInHeading: number;
  skipAlreadyCorrect: number;
}

type PhaseResult =
  | "continue"
  | "handled"
  | "skip-empty"
  | "skip-excluded"
  | "skip-already-correct";

// ═══════════════════════════════════════════════════════════════════════════
// Truncate utility for log lines
// ═══════════════════════════════════════════════════════════════════════════

function snippet(s: string, n: number = 60): string {
  const t = (s || "").trim().replace(/\s+/g, ' ');
  return t.length > n ? t.substring(0, n) + '…' : t;
}

// ═══════════════════════════════════════════════════════════════════════════
// (No pre-scan. Run 25 used to have a diagnostic pre-scan that hunted for
// header candidates ahead of the main pass — that was useful when Phase 2
// existed. Now that Run 25 never demotes paragraphs, the pre-scan is
// removed.)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1 — Universal skip checks (now logs every skip with its reason)
// ═══════════════════════════════════════════════════════════════════════════

function phase1_universalSkips(
  paraText: string,
  paraStyle: string,
  styleBI: string,
  loc: string,
  counters: RunCounters
): PhaseResult {
  if (!paraText.trim()) {
    console.log(`${loc} SKIP-EMPTY  | style="${paraStyle}" styleBI="${styleBI}"`);
    counters.skipEmpty++;
    return "skip-empty";
  }
  if (shouldExclude(paraStyle)) {
    console.log(`${loc} SKIP-EXCLUDED-STYLE | style="${paraStyle}" — "${snippet(paraText)}"`);
    counters.skipShouldExclude++;
    return "skip-excluded";
  }
  if (styleBI && /^Heading[1-9]$/.test(styleBI.replace(/\s/g, ''))) {
    console.log(`${loc} SKIP-BUILTIN-HEADING | styleBI="${styleBI}" — "${snippet(paraText)}"`);
    counters.skipBuiltInHeading++;
    return "skip-excluded";
  }
  return "continue";
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 2 — REMOVED
// ═══════════════════════════════════════════════════════════════════════════
//
// Header demotion was removed because it did too much. A heuristic-based
// "this looks like a header so demote it" rule produces too many false
// positives in pharmaceutical documents (cell labels like "Sponsor:",
// "Investigator(s):" are common). And the previous preemptive variant —
// writing a numId=0 marker into every header-shaped paragraph — caused
// downstream side effects when documents had accumulated tracked changes
// from earlier test runs.
//
// The new policy: Run 25 NEVER demotes paragraphs. It only ever sets the
// style of paragraphs that have OBSERVABLE list membership (direct numPr
// in their own pPr, or an explicit list marker in their text). Plain
// paragraphs are left completely alone, even when Word.js's
// listOrNullObject reports them as "in a list" (which can happen due to
// style-linked auto-promotion via the <w:pStyle> element inside abstractNum).
//
// If a paragraph that visually shouldn't be a list item still appears
// numbered, it's because of pre-existing document state — and the user
// should clean it up via Word's UI ("right-click number → No bullets or
// numbering"). This is the regulatory-safe behavior: never make a change
// the user didn't ask for.

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 3 — Already correct
// ═══════════════════════════════════════════════════════════════════════════

function phase3_alreadyCorrect(
  paraStyle: string,
  paraText: string,
  loc: string,
  counters: RunCounters
): PhaseResult {
  if (isAnyTableListStyle(paraStyle)) {
    console.log(`${loc} SKIP-ALREADY-CORRECT | style="${paraStyle}" — "${snippet(paraText)}"`);
    counters.skipAlreadyCorrect++;
    return "skip-already-correct";
  }
  return "continue";
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 4 — Wrong context (classifier)
// ═══════════════════════════════════════════════════════════════════════════
//
// Returns a ListGroupItem if the paragraph wears a body list style (which
// is wrong inside a table cell — should be a Table:* style), else null.
//
// We no longer guard on direct <w:numPr>: the user observed that not every
// real list item has a direct numPr, and the guard was over-strict. The
// signal here is purely the paragraph STYLE — `isAnyBodyListStyle` matches
// "List Number", "List Bullet", "List Alpha 2", etc. — which is unambiguous:
// such a style has no business being applied inside a table cell.

async function classify_wrongContext(
  paraText: string,
  paraStyle: string
): Promise<{ listType: ListType; level: number; targetStyle: string } | null> {
  if (!isAnyBodyListStyle(paraStyle)) return null;

  // S4165 fix: initialise to "bullet" and only reassign for alpha/number branches
  let listType: ListType = "bullet";
  let level = 0;

  if (isBodyBulletStyle(paraStyle)) {
    // listType already "bullet" — no redundant reassignment
    level = getBulletLevel(paraText);
  } else if (isBodyAlphaStyle(paraStyle)) {
    listType = "alpha";
    level = getLevelFromBodyStyle(paraStyle);
  } else if (isBodyNumberStyle(paraStyle)) {
    listType = "number";
    level = getLevelFromBodyStyle(paraStyle);
  }

  const targetStyle = getTargetStyle(listType, level, true);
  return { listType, level, targetStyle };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 5 — Existing Word list (classifier)
// ═══════════════════════════════════════════════════════════════════════════
//
// Returns a ListGroupItem if Word.js reports the paragraph is in a list
// AND the current style does not match the expected Table:* style.
//
// The "is real list" signal here is two converging conditions:
//
//   1. listOrNullObject is non-null — Word.js considers this paragraph
//      part of a list at runtime.
//   2. listItem.listString is non-empty — the paragraph renders a visible
//      marker (number, letter, bullet glyph). Style-link auto-promoted
//      plain paragraphs typically have an EMPTY listString because they
//      were never assigned a real list position; this filter screens them
//      out without relying on direct <w:numPr>.

async function classify_existingList(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraText: string,
  paraStyle: string
): Promise<{ listType: ListType; level: number; targetStyle: string; marker: string } | null> {
  const listOrNull = para.listOrNullObject;
  listOrNull.load("isNullObject");
  await context.sync();
  if (listOrNull.isNullObject) return null;

  const list = para.list;
  list.load("levelTypes");
  const listItem = para.listItem;
  listItem.load("level, listString");
  await context.sync();

  const level = listItem.level;
  const levelTypes = list.levelTypes;
  const listString = (listItem.listString || "").trim();
  const levelType = (levelTypes && levelTypes.length > level) ? levelTypes[level] : "";

  // Real-list signal: a non-empty listString means Word is rendering a
  // visible marker for this paragraph. Empty listString = style-link
  // auto-promoted plain paragraph (the noise case we want to filter out).
  if (!listString) return null;

  const listType = classifyListItem(levelType, listString);
  if (!listType) return null;

  let finalLevel = level;
  if (listType === "bullet") finalLevel = getBulletLevel(paraText);

  const targetStyle = getTargetStyle(listType, finalLevel, true);
  if (paraStyle === targetStyle) return null;  // already correct

  return { listType, level: finalLevel, targetStyle, marker: listString };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 6 — Pattern detection (classifier)
// ═══════════════════════════════════════════════════════════════════════════
//
// Returns a ListGroupItem if the paragraph's TEXT starts with a list
// marker pattern (1., a., •) but the paragraph isn't styled as a list.
// This is the speculative phase — useful for spotting un-styled lists.

async function classify_patternMatch(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraText: string,
  paraStyle: string
): Promise<{ listType: ListType; level: number; targetStyle: string } | null> {
  // Skip if the paragraph already wears a correct table list style — that
  // means it's already handled (Phase 3 would have skipped it).
  if (isAnyTableListStyle(paraStyle)) return null;

  const isBullet = detectBullet(paraText);
  const isAlpha = detectAlphaList(paraText);
  const isNumber = detectNumberedList(paraText);
  if (!isBullet && !isNumber && !isAlpha) return null;

  let listType: ListType;
  let level: number;
  if (isBullet) {
    listType = "bullet";
    level = getBulletLevel(paraText);
  } else if (isAlpha) {
    listType = "alpha";
    level = await getIndentLevel(para, context);
  } else {
    listType = "number";
    level = await getIndentLevel(para, context);
  }

  const targetStyle = getTargetStyle(listType, level, true);
  return { listType, level, targetStyle };
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-paragraph classifier — produces a flagger item or returns "skip"
// ═══════════════════════════════════════════════════════════════════════════
//
// Returns one of:
//   - { kind: "skip" }            — paragraph is uninteresting (empty,
//                                   excluded style, built-in heading,
//                                   already-correct list style)
//   - { kind: "ok-list", ...}     — paragraph is correctly-styled list item;
//                                   acts as a non-flag list item that should
//                                   NOT close an in-progress mismatch group
//                                   if it shares the same listType, but
//                                   should close it otherwise. For grouping,
//                                   we treat this as "close mismatch group".
//   - { kind: "flag", item }      — paragraph should be flagged
//
// (For simplicity we don't model "ok-list" — a correctly-styled list item
// is treated the same as any other non-flag paragraph: it CLOSES the
// in-progress flag group. So adjacent flag items are grouped only when no
// correctly-styled item separates them.)

interface ClassifyResult {
  kind: "skip" | "flag";
  item?: ListGroupItem;
}

async function classifyCellParagraph(
  para: Word.Paragraph,
  context: Word.RequestContext,
  loc: string,
  counters: RunCounters
): Promise<ClassifyResult> {
  para.load("text, style, styleBuiltIn, uniqueLocalId");
  await context.sync();

  const paraText = para.text || "";
  const paraStyle = para.style || "";
  const styleBI = para.styleBuiltIn || "";
  counters.totalChecked++;

  // Phase 1 — universal skips
  const r1 = phase1_universalSkips(paraText, paraStyle, styleBI, loc, counters);
  if (r1 !== "continue") return { kind: "skip" };

  // Phase 3 — already correct (a correctly-styled list item closes any
  // in-progress flag group, just like any other non-flag paragraph)
  if (phase3_alreadyCorrect(paraStyle, paraText, loc, counters) === "skip-already-correct") {
    return { kind: "skip" };
  }

  // Phase 4 — wrong context
  const wc = await classify_wrongContext(paraText, paraStyle);
  if (wc) {
    console.log(`${loc} WRONG-CTX-FLAG | "${paraStyle}" should be "${wc.targetStyle}" — "${snippet(paraText)}"`);
    counters.wrongContextFixed++;
    return {
      kind: "flag",
      item: {
        kind: `wrong-ctx:${wc.listType}`,
        para, paraId: para.uniqueLocalId, paraText,
        currentStyle: paraStyle, expectedStyle: wc.targetStyle,
        level: wc.level, marker: "",
        contextLabel: "table cell",
        headerLabel: "List/bullet style mismatch (body list style in a table)",
      }
    };
  }

  // Phase 5 — existing Word list
  const ex = await classify_existingList(para, context, paraText, paraStyle);
  if (ex) {
    console.log(`${loc} EXISTING-${ex.listType.toUpperCase()}-FLAG | "${paraStyle}" should be "${ex.targetStyle}" L${ex.level} ls="${ex.marker}" — "${snippet(paraText)}"`);
    counters.existingStyled++;
    return {
      kind: "flag",
      item: {
        kind: `existing:${ex.listType}`,
        para, paraId: para.uniqueLocalId, paraText,
        currentStyle: paraStyle, expectedStyle: ex.targetStyle,
        level: ex.level, marker: ex.marker,
        contextLabel: "table cell",
        headerLabel: `List/bullet style mismatch (table cell ${ex.listType} list)`,
      }
    };
  }

  // Phase 6 — text pattern
  const pm = await classify_patternMatch(para, context, paraText, paraStyle);
  if (pm) {
    console.log(`${loc} PATTERN-${pm.listType.toUpperCase()}-FLAG | L${pm.level} should be "${pm.targetStyle}" — "${snippet(paraText)}"`);
    counters.patternsConverted++;
    return {
      kind: "flag",
      item: {
        kind: `pattern:${pm.listType}`,
        para, paraId: para.uniqueLocalId, paraText,
        currentStyle: paraStyle, expectedStyle: pm.targetStyle,
        level: pm.level, marker: "",
        contextLabel: "table cell",
        headerLabel: `Possible un-styled ${pm.listType} list (table cell)`,
        detail: "(Detected from text pattern; paragraph isn't styled as a list.)",
      }
    };
  }

  return { kind: "skip" };
}

// ═══════════════════════════════════════════════════════════════════════════
// S3776 fix: extracted inner cell-processing loop to reduce cognitive
// complexity of run25() from 26 → well below the 15 threshold.
// ═══════════════════════════════════════════════════════════════════════════

async function processCellParagraphs(
  cell: Word.TableCell,
  context: Word.RequestContext,
  flagger: ListGroupFlagger,
  counters: RunCounters,
  t: number,
  r: number,
  c: number,
): Promise<void> {
  const cellParas = cell.body.paragraphs;
  cellParas.load("items");
  await context.sync();

  for (let p = 0; p < cellParas.items.length; p++) {
    const para = cellParas.items[p];
    const loc = `[T${t + 1} R${r + 1} C${c + 1} P${p + 1}]`;
    const result = await classifyCellParagraph(para, context, loc, counters);
    if (result.kind === "flag" && result.item) {
      await flagger.add(result.item);
    } else {
      // Non-flag paragraph closes any in-progress flag group.
      await flagger.flush();
    }
  }
  // End-of-cell flush: prevents a list ending in one cell from bleeding
  // into the next cell's paragraphs.
  await flagger.flush();
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level entry point
// ═══════════════════════════════════════════════════════════════════════════

export async function run25() {
  await Word.run(async (context) => {
    try {

      const cfg = getConfig().run25_tableListBullets;

      const counters: RunCounters = {
        totalChecked: 0, existingStyled: 0, patternsConverted: 0,
        skippedCount: 0, wrongContextFixed: 0, headersDemoted: 0,
        skipEmpty: 0, skipShouldExclude: 0,
        skipBuiltInHeading: 0, skipAlreadyCorrect: 0,
      };
      const changedParas: RunDetail[] = [];

      console.log('═'.repeat(70));
      console.log('RUN 25: Table List & Bullet Styling — FLAG MODE');
      console.log('═'.repeat(70));
      console.log('Mode    : FLAG ONLY — drops a comment at each mismatch and');
      console.log('          adds a clickable Go-To entry in the QC summary.');
      console.log('          Does NOT modify paragraph styles automatically.');

      // S4624 fix: pre-compute the optional level3 segment to avoid a
      // nested template literal inside the log line.
      const level3Part = cfg.bulletStyles.level3 ? ` / ${cfg.bulletStyles.level3}` : "";
      console.log(`Bullets : expected — ${cfg.bulletStyles.level1} / ${cfg.bulletStyles.level2}${level3Part}`);
      console.log(`Numbers : expected — ${cfg.numberStyle}`);
      console.log(`Alpha   : expected — ${cfg.alphaStyle}`);
      console.log('Scope   : Table cell paragraphs ONLY');

      const tables = context.document.body.tables;
      tables.load('items');
      await context.sync();

      const totalTables = tables.items.length;
      console.log(`\nTables found: ${totalTables}`);

      if (totalTables === 0) {
        console.log('No tables in document.');
        reportResult('Table List & Bullet Styling — no tables found',
          [{ label: 'No tables in document', note: 'Nothing to process' }]);
        return;
      }

      // ── Main pass — paragraphs flow through a flagger that groups
      // contiguous list items into a single comment. The grouping scope is
      // PER CELL: each table cell is processed independently via
      // processCellParagraphs(), which also handles the end-of-cell flush. ──
      console.log('═'.repeat(70));
      console.log('MAIN PASS: processing every cell paragraph (grouped flagging)');
      console.log('═'.repeat(70) + '\n');

      const flagger = new ListGroupFlagger(context, changedParas);

      for (let t = 0; t < totalTables; t++) {
        const table = tables.items[t];
        table.rows.load('items');
        await context.sync();

        for (let r = 0; r < table.rows.items.length; r++) {
          const row = table.rows.items[r];
          row.cells.load('items');
          await context.sync();

          for (let c = 0; c < row.cells.items.length; c++) {
            await processCellParagraphs(row.cells.items[c], context, flagger, counters, t, r, c);
          }
        }
      }
      // Final flush at end of document.
      await flagger.flush();

      const flaggerStats = flagger.getStats();

      const totalFlaggedItems =
        counters.existingStyled + counters.patternsConverted +
        counters.wrongContextFixed;

      console.log('\n' + '═'.repeat(70));
      console.log('SUMMARY');
      console.log('═'.repeat(70));
      console.log(`Tables                       : ${totalTables}`);
      console.log(`Total paragraphs checked     : ${counters.totalChecked}`);
      console.log(`  Skipped (empty)            : ${counters.skipEmpty}`);
      console.log(`  Skipped (excluded style)   : ${counters.skipShouldExclude}`);
      console.log(`  Skipped (built-in heading) : ${counters.skipBuiltInHeading}`);
      console.log(`  Skipped (already correct)  : ${counters.skipAlreadyCorrect}`);
      console.log(`  Skipped (other)            : ${counters.skippedCount}`);
      console.log(`Flagged — wrong-context      : ${counters.wrongContextFixed}`);
      console.log(`Flagged — existing list      : ${counters.existingStyled}`);
      console.log(`Flagged — pattern detected   : ${counters.patternsConverted}`);
      console.log(`TOTAL FLAGGED ITEMS          : ${totalFlaggedItems}`);
      console.log(`  → emitted as ${flaggerStats.groupsEmitted} comment(s) (one per list)`);
      console.log('═'.repeat(70));

      if (flaggerStats.groupsEmitted > 0) {
        const itemNoun = totalFlaggedItems === 1 ? 'item' : 'items';
        const listNoun = flaggerStats.groupsEmitted === 1 ? 'list' : 'lists';
        reportResult(
          `Table List & Bullet — ${flaggerStats.groupsEmitted} ${listNoun} flagged (${totalFlaggedItems} ${itemNoun})`,
          [
            { label: 'Tables processed',                  count: totalTables },
            { label: 'Lists flagged with comments',       count: flaggerStats.groupsEmitted },
            { label: 'Total items inside flagged lists',  count: totalFlaggedItems },
            { label: 'Wrong-context items',               count: counters.wrongContextFixed },
            { label: 'Existing-list mismatch items',      count: counters.existingStyled },
            { label: 'Pattern-detected items',            count: counters.patternsConverted },
            ...changedParas,
          ]
        );
      } else {
        reportResult('Table List & Bullet Styling — no issues found',
          [{ label: 'All table list styles look correct', note: 'No comments added' }]);
      }
    } catch (error) {
      console.error('Error in run25:', error);
      safeAlert('Error in Table List & Bullet Styling:\n' + (error as Error).message);
    }
  });
}